(() => {
  const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  const PAGEFLIP_CDN = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js';

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[data-pfb-src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(`Không tải được script: ${src}`)), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.pfbSrc = src;
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = () => reject(new Error(`Không tải được script: ${src}`));
      document.head.appendChild(script);
    });

  const ensureLibs = async () => {
    await Promise.all([loadScript(PDFJS_CDN), loadScript(PAGEFLIP_CDN)]);

    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    }
  };

  const renderPdfToImages = async (url, { onProgress, scale = 1.6 } = {}) => {
    const pdf = await window.pdfjsLib.getDocument({ url, withCredentials: false }).promise;
    const images = [];

    for (let i = 1; i <= pdf.numPages; i += 1) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { alpha: false });

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: context, viewport }).promise;
      images.push(canvas.toDataURL('image/jpeg', 0.88));

      if (typeof onProgress === 'function') {
        onProgress(i, pdf.numPages);
      }
    }

    return images;
  };

  const buildPageElements = (images, showCover) => {
    const pages = [];

    images.forEach((src, index) => {
      const isFirst = index === 0;
      const isLast = index === images.length - 1;
      const page = document.createElement('div');
      page.className = 'pdf-flipbook__page page';

      if (showCover && (isFirst || isLast)) {
        page.classList.add('page-cover', 'hard');
        page.dataset.density = 'hard';
        page.classList.add(isFirst ? 'page-cover-top' : 'page-cover-bottom');
      }

      const content = document.createElement('div');
      content.className = 'pdf-flipbook__page-content';

      const img = document.createElement('img');
      img.className = 'pdf-flipbook__page-image';
      img.src = src;
      img.alt = `Trang ${index + 1}`;
      img.draggable = false;

      content.appendChild(img);
      page.appendChild(content);
      pages.push(page);
    });

    return pages;
  };

  class PdfFlipbook {
    constructor(root) {
      this.root = root;
      this.bookEl = root.querySelector('[data-pfb-book]');
      this.loaderEl = root.querySelector('[data-pfb-loader]');
      this.loaderTextEl = root.querySelector('[data-pfb-loader-text]');
      this.progressBarEl = root.querySelector('[data-pfb-progress-bar]');
      this.emptyEl = root.querySelector('[data-pfb-empty]');
      this.errorEl = root.querySelector('[data-pfb-error]');
      this.errorTextEl = root.querySelector('[data-pfb-error-text]');
      this.pageInfoEl = root.querySelector('[data-pfb-page-info]');
      this.prevBtn = root.querySelector('[data-pfb-prev]');
      this.nextBtn = root.querySelector('[data-pfb-next]');
      this.fullscreenBtn = root.querySelector('[data-pfb-fullscreen]');

      this.source = root.dataset.source || 'pdf';
      this.pdfUrl = (root.dataset.pdfUrl || '').trim();
      this.showCover = root.dataset.showCover !== 'false';
      this.pageWidth = Number(root.dataset.pageWidth) || 550;
      this.pageHeight = Number(root.dataset.pageHeight) || 733;
      this.images = this.#readImages();

      this.pageFlip = null;
      this.#bindUi();
    }

    #readImages() {
      try {
        const node = this.root.querySelector('[data-pfb-images]');
        const raw = node ? node.textContent : '[]';
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (error) {
        return [];
      }
    }

    #bindUi() {
      this.prevBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.pageFlip?.flipPrev();
      });

      this.nextBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.pageFlip?.flipNext();
      });

      this.fullscreenBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#toggleFullscreen();
      });

      document.addEventListener('keydown', (event) => {
        if (!this.root.classList.contains('is-ready')) return;

        const active =
          this.root.contains(document.activeElement) ||
          this.root.matches(':hover') ||
          this.root.classList.contains('pdf-flipbook--fullscreen');

        if (!active) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.pageFlip?.flipPrev();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.pageFlip?.flipNext();
        }

        if (event.key === 'Escape' && this.root.classList.contains('pdf-flipbook--fullscreen')) {
          this.#exitFullscreen();
        }
      });

      document.addEventListener('fullscreenchange', () => {
        if (!document.fullscreenElement && this.root.classList.contains('pdf-flipbook--fullscreen')) {
          this.root.classList.remove('pdf-flipbook--fullscreen');
          this.#updateFullscreenLabel(false);
          window.setTimeout(() => this.pageFlip?.update(), 120);
        }
      });
    }

    async init() {
      try {
        this.#showLoader('Đang chuẩn bị thư viện…');
        await ensureLibs();

        let pages = [];

        if (this.source === 'images') {
          pages = this.images;
          if (!pages.length) {
            this.#showEmpty('Chưa có ảnh trang. Thêm block Ảnh trong theme editor.');
            return;
          }
          this.#showLoader('Đang mở tạp chí…');
        } else {
          if (!this.pdfUrl) {
            this.#showEmpty('Chưa có link PDF. Upload file vào Files rồi dán URL vào section.');
            return;
          }
          this.#showLoader('Đang tải PDF…');
          pages = await renderPdfToImages(this.pdfUrl, {
            onProgress: (current, total) => {
              this.#showLoader(`Đang render trang ${current}/${total}…`);
              if (this.progressBarEl) {
                this.progressBarEl.style.width = `${Math.round((current / total) * 100)}%`;
              }
            },
          });
        }

        this.#createFlipbook(pages);
        this.root.classList.add('is-ready');
        this.#hideOverlays();
      } catch (error) {
        console.error('[pdf-flipbook]', error);
        this.#showError(error?.message || 'Không thể mở tạp chí. Kiểm tra lại file PDF hoặc ảnh.');
      }
    }

    #createFlipbook(images) {
      if (!window.St?.PageFlip) {
        throw new Error('Thư viện PageFlip chưa sẵn sàng.');
      }

      this.bookEl.innerHTML = '';
      const pageNodes = buildPageElements(images, this.showCover);
      for (const page of pageNodes) {
        this.bookEl.appendChild(page);
      }

      // Config giống demo chính thức: https://nodlik.github.io/StPageFlip/demo.html
      this.pageFlip = new window.St.PageFlip(this.bookEl, {
        width: this.pageWidth,
        height: this.pageHeight,
        size: 'stretch',
        minWidth: 315,
        maxWidth: 1000,
        minHeight: 420,
        maxHeight: 1350,
        maxShadowOpacity: 0.5,
        showCover: this.showCover,
        mobileScrollSupport: false,
        usePortrait: true,
        autoSize: true,
        drawShadow: true,
        flippingTime: 1000,
        useMouseEvents: true,
      });

      this.pageFlip.loadFromHTML(this.bookEl.querySelectorAll('.pdf-flipbook__page'));

      this.pageFlip.on('flip', () => this.#syncControls());
      this.pageFlip.on('changeOrientation', () => this.#syncControls());
      this.pageFlip.on('changeState', () => this.#syncControls());

      this.#syncControls();
    }

    #syncControls() {
      if (!this.pageFlip) return;

      const index = this.pageFlip.getCurrentPageIndex();
      const current = index + 1;
      const total = this.pageFlip.getPageCount();

      this.#centerSingleCover(index, total);

      if (this.pageInfoEl) {
        this.pageInfoEl.textContent = `${current} / ${total}`;
      }

      if (this.prevBtn) {
        this.prevBtn.disabled = index <= 0;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = index >= total - 1;
      }
    }

    #centerSingleCover(index, total) {
      // Khi bật bìa cứng ở chế độ landscape, StPageFlip đặt bìa trước ở nửa phải
      // và bìa sau ở nửa trái của khung sách (rộng bằng 2 trang) -> trang đơn bị
      // lệch khỏi tâm. Dịch cuốn sách để trang bìa duy nhất về giữa màn hình.
      const orientation = this.pageFlip.getOrientation ? this.pageFlip.getOrientation() : 'landscape';
      const isSingle = this.showCover && orientation === 'landscape';
      const isFront = isSingle && index === 0;
      const isBack = isSingle && index === total - 1;

      this.root.classList.toggle('pdf-flipbook--cover-front', isFront);
      this.root.classList.toggle('pdf-flipbook--cover-back', isBack);
    }

    #toggleFullscreen() {
      if (this.root.classList.contains('pdf-flipbook--fullscreen')) {
        this.#exitFullscreen();
        return;
      }

      this.root.classList.add('pdf-flipbook--fullscreen');
      this.#updateFullscreenLabel(true);

      const requestFs = this.root.requestFullscreen || this.root.webkitRequestFullscreen;
      if (requestFs) {
        Promise.resolve(requestFs.call(this.root)).catch(() => {});
      }

      window.setTimeout(() => this.pageFlip?.update(), 160);
    }

    #exitFullscreen() {
      this.root.classList.remove('pdf-flipbook--fullscreen');
      this.#updateFullscreenLabel(false);

      if (document.fullscreenElement) {
        const exitFs = document.exitFullscreen || document.webkitExitFullscreen;
        if (exitFs) {
          Promise.resolve(exitFs.call(document)).catch(() => {});
        }
      }

      window.setTimeout(() => this.pageFlip?.update(), 160);
    }

    #updateFullscreenLabel(active) {
      if (!this.fullscreenBtn) return;
      this.fullscreenBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.fullscreenBtn.textContent = active ? 'Thu nhỏ' : 'Toàn màn hình';
    }

    #showLoader(text) {
      this.emptyEl?.setAttribute('hidden', '');
      this.errorEl?.setAttribute('hidden', '');
      this.loaderEl?.removeAttribute('hidden');
      if (this.loaderTextEl && text) {
        this.loaderTextEl.textContent = text;
      }
    }

    #showEmpty(text) {
      this.loaderEl?.setAttribute('hidden', '');
      this.errorEl?.setAttribute('hidden', '');
      this.emptyEl?.removeAttribute('hidden');
      const emptyText = this.root.querySelector('[data-pfb-empty-text]');
      if (emptyText && text) emptyText.textContent = text;
    }

    #showError(text) {
      this.loaderEl?.setAttribute('hidden', '');
      this.emptyEl?.setAttribute('hidden', '');
      this.errorEl?.removeAttribute('hidden');
      if (this.errorTextEl && text) this.errorTextEl.textContent = text;
    }

    #hideOverlays() {
      this.loaderEl?.setAttribute('hidden', '');
      this.emptyEl?.setAttribute('hidden', '');
      this.errorEl?.setAttribute('hidden', '');
    }
  }

  const initAll = () => {
    const roots = document.querySelectorAll('[data-pdf-flipbook]');
    for (const root of roots) {
      if (root.dataset.pfbInitialized === 'true') continue;
      root.dataset.pfbInitialized = 'true';
      const instance = new PdfFlipbook(root);
      instance.init();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) {
      initAll();
      return;
    }

    section.querySelectorAll('[data-pdf-flipbook]').forEach((root) => {
      delete root.dataset.pfbInitialized;
    });
    initAll();
  });
})();
